import React, { useState, useEffect } from "react";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;0,900;1,400&family=Source+Serif+4:ital,opsz,wght@0,8..60,300;0,8..60,400;0,8..60,600;1,8..60,300&family=Space+Mono:wght@400;700&display=swap');

  * { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --ink: #0f0e0c;
    --paper: #f5f0e8;
    --paper-dark: #ede7d6;
    --steel: #2c3e50;
    --steel-mid: #4a6274;
    --accent: #b5451b;
    --accent-light: #e8623a;
    --gold: #c9943a;
    --gold-light: #f0c96e;
    --rule: #d4c9b0;
  }

  .article-root {
    background: var(--paper);
    min-height: 100vh;
    font-family: 'Source Serif 4', Georgia, serif;
    color: var(--ink);
    position: relative;
    overflow-x: hidden;
  }

  /* HEADER */
  .article-header {
    background: var(--steel);
    padding: 0;
    position: relative;
    overflow: hidden;
  }

  .header-noise {
    position: absolute;
    inset: 0;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.06'/%3E%3C/svg%3E");
    opacity: 0.4;
    pointer-events: none;
  }

  .header-stripe {
    height: 4px;
    background: linear-gradient(90deg, var(--accent) 0%, var(--gold) 50%, var(--accent) 100%);
  }

  .header-inner {
    max-width: 900px;
    margin: 0 auto;
    padding: 48px 40px 40px;
    position: relative;
    z-index: 1;
  }

  .journal-label {
    font-family: 'Space Mono', monospace;
    font-size: 10px;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    color: var(--gold-light);
    opacity: 0.8;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .journal-label::before,
  .journal-label::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--gold);
    opacity: 0.3;
  }

  .article-title {
    font-family: 'Playfair Display', serif;
    font-size: clamp(24px, 4vw, 44px);
    font-weight: 900;
    line-height: 1.15;
    color: #fff;
    margin-bottom: 28px;
    letter-spacing: -0.02em;
  }

  .article-title .accent-word {
    color: var(--gold-light);
    font-style: italic;
  }

  .author-block {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 16px 0;
    border-top: 1px solid rgba(201,148,58,0.3);
  }

  .author-avatar {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--accent), var(--gold));
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Playfair Display', serif;
    font-size: 18px;
    font-weight: 700;
    color: #fff;
    flex-shrink: 0;
  }

  .author-info {
    display: flex;
    flex-direction: column;
  }

  .author-name {
    font-family: 'Playfair Display', serif;
    font-size: 15px;
    font-weight: 700;
    font-style: italic;
    color: #fff;
  }

  .author-role {
    font-family: 'Space Mono', monospace;
    font-size: 9px;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: var(--gold-light);
    opacity: 0.7;
    margin-top: 2px;
  }

  /* HERO IMAGE */
  .hero-image-section {
    position: relative;
    width: 100%;
    overflow: hidden;
    background: var(--steel);
  }

  .hero-image-section img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    opacity: 0.75;
    filter: sepia(20%) contrast(1.1);
  }

  .hero-caption {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 20px 40px 16px;
    background: linear-gradient(to top, rgba(15,14,12,0.85) 0%, transparent 100%);
  }

  .hero-caption-text {
    font-family: 'Space Mono', monospace;
    font-size: 10px;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    color: var(--gold-light);
    opacity: 0.9;
  }

  /* MAIN BODY */
  .article-body {
    max-width: 900px;
    margin: 0 auto;
    padding: 0 40px 80px;
  }

  /* ABSTRACT */
  .abstract-block {
    margin: 48px 0 40px;
    padding: 32px 36px;
    background: var(--paper-dark);
    border-left: 4px solid var(--accent);
    position: relative;
    box-shadow: 4px 4px 0 rgba(181,69,27,0.08);
  }

  .abstract-label {
    font-family: 'Space Mono', monospace;
    font-size: 9px;
    letter-spacing: 0.35em;
    text-transform: uppercase;
    color: var(--accent);
    margin-bottom: 14px;
    font-weight: 700;
  }

  .abstract-text {
    font-size: 15px;
    line-height: 1.8;
    color: var(--steel);
    font-style: italic;
    font-weight: 300;
  }

  /* SECTION */
  .section {
    margin-bottom: 40px;
    padding-bottom: 40px;
    border-bottom: 1px solid var(--rule);
    animation: fadeUp 0.5s ease both;
  }

  .section:last-child {
    border-bottom: none;
  }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(16px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .section-number {
    font-family: 'Space Mono', monospace;
    font-size: 11px;
    letter-spacing: 0.15em;
    color: var(--gold);
    font-weight: 700;
    margin-bottom: 6px;
  }

  .section-title {
    font-family: 'Playfair Display', serif;
    font-size: clamp(18px, 2.5vw, 26px);
    font-weight: 700;
    color: var(--steel);
    margin-bottom: 18px;
    line-height: 1.25;
    border-bottom: 2px solid var(--rule);
    padding-bottom: 10px;
  }

  .section-body {
    font-size: 16px;
    line-height: 1.85;
    color: #2a2a2a;
    font-weight: 300;
  }

  .section-body p {
    margin-bottom: 14px;
  }

  .section-body strong {
    color: var(--steel);
    font-weight: 600;
  }

  /* UNDERLINED KEY PHRASE */
  .key-phrase {
    text-decoration: underline;
    text-decoration-color: var(--gold);
    text-underline-offset: 3px;
    font-weight: 600;
    color: var(--steel);
  }

  /* BULLET LIST */
  .bullet-list {
    list-style: none;
    margin: 14px 0 14px 8px;
    padding: 0;
  }

  .bullet-list li {
    padding: 6px 0 6px 24px;
    position: relative;
    font-size: 15.5px;
    line-height: 1.7;
    color: #2a2a2a;
    font-weight: 300;
  }

  .bullet-list li::before {
    content: '';
    position: absolute;
    left: 0;
    top: 15px;
    width: 10px;
    height: 2px;
    background: var(--accent);
  }

  /* STAT CALLOUT */
  .stat-callout {
    margin: 32px 0;
    padding: 24px 28px;
    background: var(--steel);
    color: #fff;
    display: flex;
    align-items: center;
    gap: 24px;
    position: relative;
    overflow: hidden;
  }

  .stat-callout::before {
    content: '';
    position: absolute;
    right: -20px;
    top: -20px;
    width: 120px;
    height: 120px;
    border-radius: 50%;
    background: rgba(201,148,58,0.12);
  }

  .stat-num {
    font-family: 'Playfair Display', serif;
    font-size: 42px;
    font-weight: 900;
    color: var(--gold-light);
    line-height: 1;
    flex-shrink: 0;
  }

  .stat-desc {
    font-family: 'Source Serif 4', serif;
    font-size: 14px;
    line-height: 1.6;
    color: rgba(255,255,255,0.85);
    font-style: italic;
  }

  /* PULL QUOTE */
  .pull-quote {
    margin: 36px -12px;
    padding: 28px 32px;
    border-top: 3px solid var(--gold);
    border-bottom: 3px solid var(--gold);
    text-align: center;
  }

  .pull-quote-text {
    font-family: 'Playfair Display', serif;
    font-size: clamp(16px, 2vw, 21px);
    font-style: italic;
    line-height: 1.6;
    color: var(--steel);
  }

  /* TABLE OF CONTENTS */
  .toc-sidebar {
    background: var(--paper-dark);
    border: 1px solid var(--rule);
    padding: 24px 28px;
    margin: 0 0 44px;
  }

  .toc-title {
    font-family: 'Space Mono', monospace;
    font-size: 10px;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    color: var(--steel-mid);
    margin-bottom: 14px;
    font-weight: 700;
  }

  .toc-list {
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .toc-item {
    display: flex;
    align-items: baseline;
    gap: 8px;
    font-size: 13px;
    color: var(--steel);
    cursor: pointer;
    padding: 3px 0;
    transition: color 0.2s;
  }

  .toc-item:hover {
    color: var(--accent);
  }

  .toc-num {
    font-family: 'Space Mono', monospace;
    font-size: 10px;
    color: var(--gold);
    flex-shrink: 0;
  }

  /* FOOTER */
  .article-footer {
    background: var(--steel);
    padding: 32px 40px;
    text-align: center;
  }

  .footer-logo {
    font-family: 'Space Mono', monospace;
    font-size: 10px;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    color: var(--gold-light);
    opacity: 0.6;
  }

  /* RESPONSIVE */
  @media (max-width: 600px) {
    .header-inner, .article-body, .hero-caption, .article-footer { padding-left: 20px; padding-right: 20px; }
    .abstract-block { padding: 22px 20px; }
    .stat-callout { flex-direction: column; gap: 8px; text-align: center; }
    .hero-image-section { height: 220px; }
  }
`;

const sections = [
  {
    num: "01",
    title: "Introduction",
    content: (
      <>
        <p>
          Steel has long been the backbone of industrial development and national progress. From building modern cities and critical infrastructure to enabling manufacturing and technological advancement, steel forms the structural foundation upon which nations grow and prosper. Yet beyond its physical strength lies a deeper significance—steel symbolizes resilience, endurance, and the unwavering commitment to serve society.
        </p>
        <p>
          The theme <span className="key-phrase">“Steel in Spirit, Strength in Service”</span> reflects the ethos of India’s steel community—particularly the thousands of entrepreneurs, workers, engineers, and innovators whose dedication continues to strengthen the nation’s industrial base. It captures the spirit of the secondary steel sector: dynamic, resilient, and deeply committed to supporting India’s journey toward sustainable growth, technological advancement, and global competitiveness.
        </p>
      </>
    ),
  },
  {
    num: "02",
    title: "Steel: The Backbone of Nation Building",
    content: (
      <>
        <p>
          Steel is not merely a commodity; it is a strategic resource that drives economic transformation and underpins modern civilization. As a fundamental material for infrastructure and industrial development, steel plays a decisive role in shaping the growth trajectory of emerging and developed economies alike. Its strength, versatility, and durability make it indispensable for building resilient infrastructure and sustaining long-term economic progress.
        </p>
        <p>
          Across India, steel forms the backbone of development by supporting the construction of highways, railways, bridges, housing, ports, power plants, and urban infrastructure. It is equally vital to sectors such as automobile manufacturing, engineering, defence, energy, and heavy machinery. Every kilometre of railway track connecting cities, every bridge enhancing mobility, every industrial facility boosting productivity, and every resilient structure standing the test of time reflects the reliability and enduring strength of steel.
        </p>
        <p>
          Over the past decades, India’s steel sector has emerged as one of the principal pillars of the nation’s economic expansion and industrial capability. As the country continues its rapid urbanization, infrastructure development, and manufacturing growth, the demand for steel is expected to rise steadily. This expanding demand highlights the strategic importance of strengthening domestic steel production capacity and advancing technological capabilities across the sector.
        </p>
        <p>
          A particularly significant contribution comes from the secondary steel industry, driven by induction furnaces, electric melting technologies, and rolling mills spread across the country. This segment plays a crucial role in meeting domestic steel requirements while promoting efficient recycling of scrap and supporting the principles of a circular economy. It also generates large-scale employment, fosters regional entrepreneurship, and contributes to balanced industrial development across multiple states and industrial clusters.
        </p>
        <p>
          Through its adaptability, innovation, and resilience in the face of evolving market dynamics and policy frameworks, the secondary steel sector continues to reinforce India’s industrial ecosystem. In doing so, the industry truly embodies the spirit of “Steel in Spirit”—strong in character, adaptable to change, and forward-looking in its commitment to building a robust and self-reliant industrial future.
        </p>
      </>
    ),
  },
  {
    num: "03",
    title: "AIIFA: Strengthening the Secondary Steel Sector",
    content: (
      <>
        <p>
          The AIIFA Sustainable Steel Manufacturers Association stands as a prominent and influential voice of India’s secondary steel industry. As a premier national industry body, AIIFA represents a vast network of steel producers, induction furnace units, rolling mills, fabricators, and allied industries that collectively form the backbone of the country’s secondary steel manufacturing ecosystem.
        </p>
        <p>
          With a strong presence across major industrial clusters in India, the association plays a crucial role in uniting stakeholders and advancing the interests of the sector at both national and regional levels. Over the years, AIIFA has emerged as a vital bridge between industry and government, facilitating constructive dialogue with policymakers, regulatory authorities, and institutional stakeholders.
        </p>
        <p>
          Through its initiatives and programs, AIIFA works to:
        </p>
        <ul className="bullet-list">
          <li>Promote sustainable and responsible steel manufacturing practices</li>
          <li>Encourage technological modernization and innovation</li>
          <li>Represent industry concerns before government and regulatory bodies</li>
          <li>Facilitate knowledge sharing, collaboration, and capacity building</li>
          <li>Promote skill development and technical awareness</li>
          <li>Advocate policies that strengthen competitiveness, efficiency, and market access</li>
        </ul>
        <p>
          Beyond policy advocacy, AIIFA fosters a culture of cooperation and collective progress by bringing together manufacturers, technology providers, researchers, and policymakers. Its efforts reflect a strong commitment to ensuring that the secondary steel sector continues to contribute meaningfully to India’s economic development while embracing sustainability and technological progress.
        </p>
      </>
    ),
  },
  {
    num: "04",
    title: "Resilience of the Secondary Steel Industry",
    content: (
      <>
        <p>
          The secondary steel sector, particularly the induction furnace route, has consistently demonstrated remarkable resilience and adaptability. Over the years, this segment has evolved into a vital component of India’s manufacturing ecosystem, responding dynamically to shifting market conditions, technological developments, and policy frameworks.
        </p>
        <p>
          Operating within a highly competitive environment, the industry faces challenges such as fluctuations in raw material prices, volatility in global scrap markets, rising energy costs, and logistical constraints. Yet thousands of steel units across the country continue to operate with determination, innovation, and a strong commitment to meeting domestic steel demand.
        </p>
        <p>
          A large proportion of these enterprises belong to the MSME sector, making the secondary steel industry a powerful driver of inclusive economic growth. Spread across numerous industrial clusters, these units support regional economies and create employment opportunities for skilled and semi-skilled workers.
        </p>
        <p>
          Their contributions include:
        </p>
        <ul className="bullet-list">
          <li>Efficient recycling of steel scrap</li>
          <li>Promotion of circular economy practices</li>
          <li>Large-scale employment generation</li>
          <li>Supply of steel to construction, infrastructure, engineering, and manufacturing sectors</li>
          <li>Strengthening regional industrial ecosystems</li>
        </ul>
        <p>
          By embracing modernization, improved quality standards, and sustainable production practices, the sector continues to remain competitive in a rapidly evolving global market.
        </p>
      </>
    ),
  },
  {
    num: "05",
    title: "Sustainability and Responsible Steelmaking",
    content: (
      <>
        <p>
          As the world intensifies efforts to address climate change, sustainability has become a central priority for the global steel industry. Producers are increasingly expected to adopt cleaner technologies, improve energy efficiency, and align operations with global climate commitments.
        </p>
        <p>
          India’s secondary steel industry holds a unique advantage in this transition. Scrap-based steel production through electric melting processes already contributes to lower emissions compared to traditional steelmaking routes.
        </p>
        <p>
          Key sustainability initiatives include:
        </p>
        <ul className="bullet-list">
          <li>Scrap-based steel production, reducing carbon emissions and conserving natural resources</li>
          <li>Adoption of energy-efficient furnace technologies</li>
          <li>Integration of renewable energy sources such as solar power</li>
          <li>Improved resource efficiency and waste management</li>
          <li>Continuous technology upgrades and modernization</li>
        </ul>
        <p>
          These initiatives not only support environmental sustainability but also strengthen India’s progress toward green steel, circular economy practices, and sustainable industrial development.
        </p>
      </>
    ),
  },
  {
    num: "06",
    title: "Service to the Nation Through Industry",
    content: (
      <>
        <p>
          The steel sector exemplifies the principle of strength in service by supporting the nation’s development in multiple ways.
        </p>
        <p>
          Steel plays a critical role in enabling large-scale infrastructure development, industrial growth, and economic transformation. From highways and rail networks to ports, power plants, and urban housing, steel provides the structural strength necessary for modern development.
        </p>
        <p>
          The industry contributes by:
        </p>
        <ul className="bullet-list">
          <li>Supplying materials for infrastructure expansion</li>
          <li>Enabling industrial and manufacturing growth</li>
          <li>Generating employment across urban and rural regions</li>
          <li>Supporting national initiatives such as Make in India and Atmanirbhar Bharat</li>
          <li>Driving regional economic development through industrial clusters</li>
        </ul>
        <p>
          Behind every ton of steel produced lies the dedication of countless workers, engineers, technicians, and entrepreneurs who ensure quality, efficiency, and innovation across the value chain.
        </p>
      </>
    ),
  },
  {
    num: "07",
    title: "Collaboration for Future Growth",
    content: (
      <>
        <p>
          The future of the steel industry depends on strong collaboration among government institutions, industry stakeholders, technology providers, and research organizations.
        </p>
        <p>
          Addressing challenges related to raw material availability, energy costs, logistics infrastructure, and sustainability requires coordinated efforts and shared innovation.
        </p>
        <p>
          Key priorities include:
        </p>
        <ul className="bullet-list">
          <li>Stable access to raw materials and scrap</li>
          <li>Rationalization of energy costs and logistics infrastructure</li>
          <li>Technology upgradation and modernization</li>
          <li>Innovation in green steel technologies</li>
          <li>Strengthening global competitiveness of Indian steel manufacturers</li>
        </ul>
        <p>
          Industry platforms and forums organized by bodies such as AIIFA Sustainable Steel Manufacturers Association provide valuable opportunities for dialogue, policy engagement, and knowledge exchange.
        </p>
      </>
    ),
  },
  {
    num: "08",
    title: "Looking Ahead",
    content: (
      <>
        <p>
          India’s steel demand is expected to grow significantly in the coming decades as the nation continues its rapid urbanization and infrastructure expansion. Large-scale investments in transportation networks, housing, renewable energy, and smart cities will further drive demand for steel.
        </p>
        <p>
          At the same time, manufacturing expansion supported by initiatives such as Make in India and Atmanirbhar Bharat will reinforce the strategic importance of the steel sector.
        </p>
        <p>
          This growth presents both opportunity and responsibility. The industry must continue to prioritize technological advancement, energy efficiency, sustainable production practices, and skill development to remain globally competitive while contributing responsibly to national development.
        </p>
        <p>
          With resilience, innovation, and sustainability as guiding principles, India’s steel industry is well positioned to shape the nation’s industrial future.
        </p>
      </>
    ),
  },
  {
    num: "09",
    title: "Conclusion",
    content: (
      <>
        <p>
          The theme <span className="key-phrase">“Steel in Spirit, Strength in Service”</span> captures the enduring values that define India’s steel industry. It reflects the resilience of entrepreneurs, the dedication of workers, and the collective commitment of industry leaders to national development.
        </p>
        <p>
          For decades, the steel sector has played a central role in shaping India’s infrastructure, strengthening its manufacturing base, and supporting economic progress. Through innovation, sustainability, and collaboration, the industry will continue to forge the foundations of future growth.
        </p>
        <p>
          Ultimately, the true strength of the steel industry lies not only in the material it produces but in the values it upholds—resilience, integrity, innovation, and service to the nation. Just as steel provides the structural framework for modern civilization, the spirit of service within the industry ensures that this strength is used to build a stronger, more inclusive, and sustainable future for India.
        </p>
      </>
    ),
  },
];

const tocItems = [
  { num: "01", label: "Introduction" },
  { num: "02", label: "Steel: The Backbone of Nation Building" },
  { num: "03", label: "AIIFA: Strengthening the Secondary Steel Sector" },
  { num: "04", label: "Resilience of the Secondary Steel Industry" },
  { num: "05", label: "Sustainability and Responsible Steelmaking" },
  { num: "06", label: "Service to the Nation Through Industry" },
  { num: "07", label: "Collaboration for Future Growth" },
  { num: "08", label: "Looking Ahead" },
  { num: "09", label: "Conclusion" },
];

const Article4 = () => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(true);
  }, []);

  const scrollTo = (idx) => {
    const el = document.getElementById(`section-${idx}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="article-root">
      <style>{styles}</style>

      {/* HEADER */}
      <header className="article-header">
        <div className="header-noise" />
        <div className="header-stripe" />
        <div className="header-inner">
          <div className="journal-label">
            AIIFA Research Publication · May 2026 · Sustainable Steel Series
          </div>
          <h1 className="article-title">
            Steel in <span className="accent-word">Spirit</span>, Strength in Service
          </h1>
          <div className="author-block">
            <div className="author-avatar">YM</div>
            <div className="author-info">
              <span className="author-name">Yogesh Mandhani</span>
              <span className="author-role">
                President, AIIFA Sustainable Steel Manufacturers Association
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* HERO IMAGE - You can change the image URL if needed */}
      <div className="hero-image-section">
        <img
          src="https://res.cloudinary.com/dxvbzgdgw/image/upload/v1775720177/WhatsApp_Image_2026-04-09_at_12.42.25_ecphzr.jpg"
          alt="Steel manufacturing facility with molten steel"
        />
        <div className="hero-caption">
          <span className="hero-caption-text">
            Secondary steel production · India's resilient industrial ecosystem
          </span>
        </div>
      </div>

      {/* BODY */}
      <main className="article-body">
        {/* ABSTRACT */}
        <div className="abstract-block">
          <div className="abstract-label">Abstract</div>
          <p className="abstract-text">
            Steel symbolizes resilience, endurance, and service to the nation. This article explores the vital role of India’s secondary steel sector—powered by induction furnaces, rolling mills, and thousands of MSME entrepreneurs—in nation building. Under the theme “Steel in Spirit, Strength in Service,” it highlights the contributions of the sector to infrastructure, employment, circular economy practices, and sustainable industrial growth. The AIIFA Sustainable Steel Manufacturers Association continues to champion technological advancement, policy advocacy, and responsible steelmaking to support India’s journey toward self-reliance and global competitiveness.
          </p>
        </div>

        {/* STAT CALLOUT - Optional: You can change or remove this */}
        <div className="stat-callout">
          <div className="stat-num">47%</div>
          <div className="stat-desc">
            of India's steel production comes from the secondary sector, driving employment, scrap recycling, and regional industrial growth.
          </div>
        </div>

        {/* TABLE OF CONTENTS */}
        <div className="toc-sidebar">
          <div className="toc-title">Contents</div>
          <ul className="toc-list">
            {tocItems.map((item, i) => (
              <li key={i} className="toc-item" onClick={() => scrollTo(i)}>
                <span className="toc-num">{item.num}</span>
                <span>{item.label}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* PULL QUOTE */}
        <div className="pull-quote">
          <p className="pull-quote-text">
            "The true strength of the steel industry lies not only in the material it produces but in the values it upholds—resilience, integrity, innovation, and service to the nation."
          </p>
        </div>

        {/* SECTIONS */}
        {sections.map((sec, idx) => (
          <section
            key={idx}
            id={`section-${idx}`}
            className="section"
            style={{ animationDelay: `${idx * 0.05}s` }}
          >
            <div className="section-number">Section {sec.num}</div>
            <h2 className="section-title">{sec.title}</h2>
            <div className="section-body">{sec.content}</div>
          </section>
        ))}
      </main>

      {/* FOOTER - Uncomment if needed */}
      {/* <footer className="article-footer">
        <div className="footer-logo">AIIFA · Sustainable Steel Manufacturers Association · All Rights Reserved</div>
      </footer> */}
    </div>
  );
};

export default Article4;